import { adminFetch } from "./_client";
import type { Inspection } from "./types";


export function fetchInspections(token: string, params: { car_id?: number; trip_id?: number; page?: number }) {
  const qs = new URLSearchParams();
  if (params.car_id) qs.set("car_id", String(params.car_id));
  if (params.trip_id) qs.set("trip_id", String(params.trip_id));
  if (params.page) qs.set("page", String(params.page));
  return adminFetch<{ inspections: Inspection[]; total: number; page: number; limit: number }>(`/admin/inspections?${qs}`, token);
}


export function createInspection(token: string, data: {
  car_id: number;
  trip_id?: number | null;
  inspection_type: string;
  status?: string;
  inspection_date?: string;
  inspector_name: string;
  mileage_reading?: number | null;
  fuel_level?: string;
  condition_notes?: string;
  damage_observations?: string;
  photos?: { url?: string; objectPath?: string; caption?: string }[];
}) {
  return adminFetch<Inspection>("/admin/inspections", token, { method: "POST", body: JSON.stringify(data) });
}


// Follow-up #3702 — upload an inspection photo to private object storage
// instead of pasting an image URL. Mirrors the renter/host flow: request a
// presigned PUT URL, upload the file directly to storage, then echo the
// returned `objectPath` back in the create payload.
export function requestInspectionUploadUrl(token: string, contentType: string) {
  return adminFetch<{ uploadURL: string; objectPath: string }>(
    "/admin/inspections/request-upload-url",
    token,
    { method: "POST", body: JSON.stringify({ contentType }) },
  );
}

const ALLOWED_INSPECTION_UPLOAD_TYPES = ["image/jpeg", "image/png", "image/webp"];

export async function uploadInspectionPhoto(token: string, file: File): Promise<{ objectPath: string }> {
  const contentType = ALLOWED_INSPECTION_UPLOAD_TYPES.includes(file.type) ? file.type : "image/jpeg";
  const { uploadURL, objectPath } = await requestInspectionUploadUrl(token, contentType);
  const putRes = await fetch(uploadURL, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": contentType },
  });
  if (!putRes.ok) {
    throw new Error("Failed to upload photo to storage");
  }
  return { objectPath };
}


export function updateInspection(token: string, id: number, data: Record<string, unknown>) {
  return adminFetch<Inspection>(`/admin/inspections/${id}`, token, { method: "PUT", body: JSON.stringify(data) });
}


export function deleteInspection(token: string, id: number) {
  return adminFetch<{ success: boolean }>(`/admin/inspections/${id}`, token, { method: "DELETE" });
}
